/**
 * Menu - Main Menu UI
 * 
 * Casino/Vampire Survivors inspired satisfying UI.
 * Features hover effects, animations, and audio feedback.
 */

import { Difficulty } from '../game/StateManager';

/** Menu callback type */
export type MenuSelectCallback = (difficulty: Difficulty) => void;

/** Difficulty button config */
interface DifficultyButton {
    key: Difficulty;
    label: string;
    color: string;
    hoverColor: string;
}

const DIFFICULTIES: DifficultyButton[] = [
    { key: 'easy', label: 'COZY', color: '#ff7ab8', hoverColor: '#ff98c9' },
    { key: 'normal', label: 'MAIN STAGE', color: '#5dbbff', hoverColor: '#82ccff' },
    { key: 'hard', label: 'FINAL BOSS', color: '#9b6dff', hoverColor: '#b494ff' },
];

/**
 * Menu class - main menu overlay
 */
export class Menu {
    private container: HTMLDivElement;
    private onSelect?: MenuSelectCallback;
    private buttons: HTMLButtonElement[] = [];
    
    // Audio callback (to be wired up)
    private onHover?: () => void;
    private onClick?: () => void;

    constructor(parentElement: HTMLElement) {
        this.container = document.createElement('div');
        this.container.id = 'game-menu';
        this.container.innerHTML = this.getHTML();
        parentElement.appendChild(this.container);

        this.applyStyles();
        this.setupInteractions();
    }

    /**
     * Set callback for difficulty selection
     */
    setOnSelect(callback: MenuSelectCallback): void {
        this.onSelect = callback;
    }

    /**
     * Set audio callbacks
     */
    setAudioCallbacks(onHover: (() => void) | undefined, onClick: (() => void) | undefined): void {
        if (onHover !== undefined) {
            this.onHover = onHover;
        }
        if (onClick !== undefined) {
            this.onClick = onClick;
        }
    }

    /**
     * Get menu HTML
     */
    private getHTML(): string {
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        const controlsText = isTouchDevice 
            ? 'Drag Idol • Arc Motion to Pose' 
            : 'Drag Idol • A/D to Pose';

        const buttonsHTML = DIFFICULTIES.map(d => `
            <button class="menu-btn" data-difficulty="${d.key}" style="--btn-color: ${d.color}; --btn-hover: ${d.hoverColor}">
                <span class="menu-btn-text">${d.label}</span>
                <span class="menu-btn-glow"></span>
            </button>
        `).join('');

        return `
            <div class="menu-background">
                <div class="menu-particles"></div>
            </div>
            <div class="menu-content">
                <div class="menu-title">
                    <span class="title-t">IDOL</span><span class="title-dash">☆</span><span class="title-stacker">STACKER</span>
                </div>
                <div class="menu-subtitle">Build the anime dream stage.</div>
                <div class="menu-buttons">
                    ${buttonsHTML}
                </div>
                <div class="menu-controls">${controlsText}</div>
            </div>
        `;
    }

    /**
     * Apply CSS styles
     */
    private applyStyles(): void {
        const style = document.createElement('style');
        style.id = 'menu-styles';
        style.textContent = `
            #game-menu {
                --ink: #f5f7ff;
                --soft: rgba(245, 247, 255, 0.76);
                --panel: rgba(18, 22, 46, 0.78);
                --line: rgba(255, 255, 255, 0.14);
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                z-index: 200;
                font-family: 'Trebuchet MS', 'Segoe UI', sans-serif;
                color: var(--ink);
            }

            .menu-background {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background:
                    radial-gradient(circle at 15% 20%, rgba(255, 122, 184, 0.22), transparent 40%),
                    radial-gradient(circle at 80% 18%, rgba(93, 187, 255, 0.2), transparent 42%),
                    radial-gradient(circle at 50% 90%, rgba(155, 109, 255, 0.16), transparent 50%),
                    linear-gradient(155deg, #0f1227 0%, #181e45 46%, #0a0d1d 100%);
            }

            .menu-particles {
                position: absolute;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-image: 
                    radial-gradient(2px 2px at 20px 30px, rgba(255, 192, 230, 0.55), transparent),
                    radial-gradient(2px 2px at 40px 70px, rgba(173, 226, 255, 0.45), transparent),
                    radial-gradient(1px 1px at 90px 40px, rgba(255,255,255,0.6), transparent),
                    radial-gradient(2px 2px at 130px 80px, rgba(203, 172, 255, 0.45), transparent),
                    radial-gradient(1px 1px at 160px 120px, rgba(255, 236, 163, 0.6), transparent);
                background-size: 200px 150px;
                animation: twinkle 4s ease-in-out infinite;
            }

            @keyframes twinkle {
                0%, 100% { opacity: 0.5; }
                50% { opacity: 1; }
            }

            .menu-content {
                position: relative;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 20px;
                backdrop-filter: saturate(120%);
            }

            .menu-title {
                font-size: clamp(48px, 12vw, 72px);
                font-weight: bold;
                margin-bottom: 10px;
                letter-spacing: 2px;
                animation: float 3s ease-in-out infinite;
            }

            @keyframes float {
                0%, 100% { transform: translateY(0); }
                50% { transform: translateY(-10px); }
            }

            .title-t {
                color: #ffd8ea;
                text-shadow: 0 0 20px rgba(255, 122, 184, 0.55);
            }

            .title-dash {
                color: #ffe66d;
                text-shadow: 0 0 20px rgba(255, 230, 109, 0.55);
                margin: 0 8px;
            }

            .title-stacker {
                color: #b9dcff;
                text-shadow: 0 0 20px rgba(93, 187, 255, 0.55);
            }

            .menu-subtitle {
                font-size: 16px;
                color: var(--soft);
                margin-bottom: 34px;
                letter-spacing: 2px;
                text-transform: uppercase;
            }

            .menu-buttons {
                display: flex;
                flex-direction: column;
                gap: 15px;
                margin-bottom: 30px;
                padding: 18px;
                border-radius: 18px;
                background: var(--panel);
                border: 1px solid var(--line);
                box-shadow: 0 20px 55px rgba(0, 0, 0, 0.35);
            }

            .menu-btn {
                position: relative;
                min-width: min(78vw, 360px);
                padding: 18px 32px;
                font-size: 20px;
                font-weight: bold;
                font-family: inherit;
                color: white;
                background: var(--btn-color);
                border: none;
                border-radius: 14px;
                cursor: pointer;
                overflow: hidden;
                transition: transform 0.15s ease, box-shadow 0.15s ease;
                border: 1px solid rgba(255,255,255,0.18);
                box-shadow: 0 10px 28px rgba(0,0,0,0.26);
                letter-spacing: 1px;
            }

            .menu-btn:hover {
                transform: scale(1.05);
                box-shadow: 0 6px 25px rgba(0,0,0,0.4), 0 0 30px color-mix(in srgb, var(--btn-color) 50%, transparent);
            }

            .menu-btn:active {
                transform: scale(0.98);
            }

            .menu-btn-text {
                position: relative;
                z-index: 1;
                text-transform: uppercase;
            }

            .menu-btn-glow {
                position: absolute;
                top: 0;
                left: -100%;
                width: 100%;
                height: 100%;
                background: linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);
                transition: left 0.5s ease;
            }

            .menu-btn:hover .menu-btn-glow {
                left: 100%;
            }

            .menu-controls {
                font-size: 14px;
                color: var(--soft);
                letter-spacing: 1px;
                background: rgba(8, 10, 22, 0.45);
                border: 1px solid rgba(255,255,255,0.09);
                border-radius: 999px;
                padding: 8px 16px;
            }

            /* Hide when not visible */
            #game-menu.hidden {
                display: none;
            }

            /* Fade in animation */
            #game-menu.fade-in {
                animation: fadeIn 0.3s ease-out;
            }

            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }

            /* Fade out animation */
            #game-menu.fade-out {
                animation: fadeOut 0.3s ease-out forwards;
            }

            @keyframes fadeOut {
                from { opacity: 1; }
                to { opacity: 0; }
            }
        `;
        document.head.appendChild(style);
    }

    /**
     * Setup button interactions
     */
    private setupInteractions(): void {
        this.buttons = Array.from(this.container.querySelectorAll('.menu-btn'));

        for (const btn of this.buttons) {
            const difficulty = btn.dataset['difficulty'] as Difficulty;

            // Hover effect
            btn.addEventListener('mouseenter', () => {
                this.onHover?.();
            });

            // Click effect
            btn.addEventListener('click', () => {
                this.onClick?.();
                this.selectDifficulty(difficulty);
            });

            // Touch feedback
            btn.addEventListener('touchstart', () => {
                btn.style.transform = 'scale(0.98)';
            });

            btn.addEventListener('touchend', () => {
                btn.style.transform = '';
            });
        }
    }

    /**
     * Handle difficulty selection
     */
    private selectDifficulty(difficulty: Difficulty): void {
        // Animate out
        this.container.classList.add('fade-out');

        setTimeout(() => {
            this.hide();
            this.onSelect?.(difficulty);
        }, 300);
    }

    /**
     * Show the menu
     */
    show(): void {
        this.container.classList.remove('hidden', 'fade-out');
        this.container.classList.add('fade-in');
    }

    /**
     * Hide the menu
     */
    hide(): void {
        this.container.classList.add('hidden');
        this.container.classList.remove('fade-in', 'fade-out');
    }

    /**
     * Check if menu is visible
     */
    isVisible(): boolean {
        return !this.container.classList.contains('hidden');
    }

    /**
     * Clean up
     */
    destroy(): void {
        this.container.remove();
        document.getElementById('menu-styles')?.remove();
    }
}
